<template>
  <div
    class="fixed bottom-4 right-4 flex flex-col"
    :class="isMinimized
      ? 'w-auto'
      : 'w-[90vw] sm:w-[400px] md:w-[450px] lg:w-[500px] max-h-[85vh] bg-white rounded-lg shadow-lg border border-gray-200'"
    :style="{ height: isMinimized ? 'auto' : 'min(80vh, 600px)' }"
  >
    <!-- Header -->
    <div
      class="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between cursor-pointer"
      :class="isMinimized ? 'rounded-full shadow-lg' : 'rounded-t-lg'"
      @click="toggleMinimize"
    >
      <div class="flex items-center space-x-2">
        <span class="font-semibold">Chat</span>
        <span v-if="selectedUserId && !isMinimized" class="text-sm bg-indigo-500 px-2 py-1 rounded">
          {{ selectedCriterionName }} - {{ selectedUserName }}
        </span>
        <!-- New messages badge when minimized -->
        <span v-if="isMinimized && totalUnread > 0" class="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
          {{ totalUnread }}
        </span>
      </div>
      <button @click.stop="toggleMinimize" class="text-white hover:bg-indigo-700 p-1 rounded">
        {{ isMinimized ? '▲' : '▼' }}
      </button>
    </div>

    <!-- Minimized state -->
    <div v-if="!isMinimized" class="flex flex-1 overflow-hidden">
      <!-- Sidebar: Thread List -->
      <div class="w-1/3 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div class="p-3 border-b border-gray-200">
          <h4 class="text-sm font-semibold text-gray-700">Chat Threads</h4>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div 
            v-for="thread in sortedThreads" 
            :key="thread.thread_id || 'general'" 
            @click="selectThread(thread)"
            class="relative px-3 py-2 cursor-pointer hover:bg-indigo-50 border-b border-gray-100 transition"
            :class="{'bg-indigo-100': currentThreadId === thread.thread_id}"
          >
            <div class="flex items-start justify-between mb-1">
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-1">
                  <span class="text-xs font-semibold text-indigo-600 truncate">{{ thread.criterion_name }}</span>
                  <span v-if="thread.unread_count > 0"
                        class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {{ thread.unread_count }}
                  </span>
                </div>
                <div class="text-sm font-medium text-gray-700 truncate">{{ thread.other_user_name }}</div>
              </div>
            </div>
            <div class="text-xs text-gray-500 truncate">{{ thread.latest_message }}</div>
            <div class="text-[10px] text-gray-400 mt-1">
              {{ formatTime(thread.latest_message_time) }}
            </div>
          </div>

          <div v-if="!threads.length" class="px-3 py-4 text-center text-sm text-gray-500">
            No threads yet
          </div>
        </div>
      </div>

      <!-- Main Chat Area -->
      <div class="w-2/3 flex flex-col bg-white">
        <!-- Messages -->
        <div class="flex-1 overflow-y-auto p-3 space-y-2" ref="messageBox">
          <div v-for="(m, idx) in messages" :key="idx" :class="['flex', m.isOwnMessage ? 'justify-end' : 'justify-start']">
            <div class="flex flex-col max-w-xs">
              <!-- History Reference (if attached) -->
              <div v-if="m.user_criterion_history || m.team_criterion_history" class="mb-2 ml-3 mr-3 text-xs">
                <div class="bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <!-- First row: icon + [TEAM] + name -->
                  <div class="flex items-center space-x-2">
                    <DocumentIcon class="w-4 h-4 text-amber-600" />
                    <span v-if="m.team_criterion_history">[TEAM]</span>
                    <span>{{ (m.user_criterion_history || m.team_criterion_history).criterion_name }}</span>
                  </div>
                  <!-- Second row: values -->
                  <div class="text-amber-700 mt-1">
                    <span v-if="(m.user_criterion_history || m.team_criterion_history).int_value != null">
                      Count changed to: <strong>{{ (m.user_criterion_history || m.team_criterion_history).int_value }}</strong>
                    </span>
                    <span v-if="(m.user_criterion_history || m.team_criterion_history).bool_value != null">
                      {{ (m.user_criterion_history || m.team_criterion_history).int_value != null ? ' · ' : '' }}
                      Status: <strong>{{ (m.user_criterion_history || m.team_criterion_history).bool_value ? 'Yes' : 'No' }}</strong>
                    </span>
                    <span v-if="(m.user_criterion_history || m.team_criterion_history).text_value">
                      {{ ((m.user_criterion_history || m.team_criterion_history).int_value != null || (m.user_criterion_history || m.team_criterion_history).bool_value != null) ? ' · ' : '' }}
                      <strong>"{{ (m.user_criterion_history || m.team_criterion_history).text_value }}"</strong>
                    </span>
                  </div>
                </div>
              </div>
              <!-- Message -->
              <div :class="['px-3 py-2 rounded-lg text-sm', m.isOwnMessage ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-800']">
                <div class="text-xs opacity-70 mb-1">{{ m.from }}
                  <span class="ml-2 opacity-60">{{ new Date(m.created_at).toLocaleString() }}</span>
                </div>
                <div class="break-words">{{ m.text }}</div>
              </div>
            </div>
          </div>
          <div v-if="!messages.length" class="text-center text-sm text-gray-400 py-4">
            No messages yet. Start a conversation!
          </div>
        </div>

        <!-- Input -->
        <div class="border-t border-gray-200 p-3 space-y-2">
          <!-- History Reference Preview (if selected) -->
          <div v-if="selectedHistoryReference" class="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs">
            <div class="flex items-center justify-between">
            <DocumentIcon class="w-4 h-4 text-amber-600" />
            <span v-if="selectedHistoryReference.source === 'team'">[TEAM] </span>
            {{ selectedHistoryReference.criterion_name }}
              <button 
                @click="selectedHistoryReference = null"
                class="text-amber-600 hover:text-amber-800 font-bold"
              >
                <XMarkIcon class="w-4 h-4" />
              </button>
            </div>
            <div class="text-amber-700 mt-1">
              <span v-if="selectedHistoryReference.int_value != null">
                Value: <strong>{{ selectedHistoryReference.int_value }}</strong>
              </span>
              <span v-if="selectedHistoryReference.bool_value != null">
                {{ selectedHistoryReference.int_value != null ? ' · ' : '' }}
                Status: <strong>{{ selectedHistoryReference.bool_value ? 'Yes' : 'No' }}</strong>
              </span>
              <span v-if="selectedHistoryReference.text_value">
                {{ (selectedHistoryReference.int_value != null || selectedHistoryReference.bool_value != null) ? ' · ' : '' }}
                <strong>"{{ selectedHistoryReference.text_value }}"</strong>
              </span>
            </div>
          </div>
          <div class="flex space-x-2">
            <input 
              v-model="messageText" 
              @keyup.enter="sendMessage"
              type="text"
              placeholder="Type a message..."
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
            />
            <button 
              @click="sendMessage"
              class="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentUser } from "@/api/users";
import { sendMessage, getChatHistory, streamChatMessages, getChatThreads } from "@/api/chat";
import { inject } from "vue";
import { DocumentIcon, XMarkIcon } from '@heroicons/vue/24/solid';

export default {
  name: "ChatWidget",
  components: { DocumentIcon, XMarkIcon},
  props: {
    initialUserId: { type: [String, Number], default: null },
  },
  computed: {
    sortedThreads() {
      return [...this.threads].sort((a, b) => {
        const timeA = new Date(a.latest_message_time).getTime();
        const timeB = new Date(b.latest_message_time).getTime();
        return timeB - timeA;
      });
    },
    totalUnread() {
      return this.threads.reduce((sum, thread) => sum + (thread.unread_count || 0), 0);
    },
  },
  setup() {
    const eventBus = inject("chatEventBus", null);
    return { eventBus };
  },
  data() {
    return {
      isMinimized: true,
      selectedUserId: null,
      selectedUserName: "",
      selectedCriterionName: "",
      messages: [],
      messageText: "",
      eventSource: null,
      currentUser: null,
      threads: [],
      userType: null,
      currentUserId: null,
      isLoadingHistory: false,
      selectedHistoryReference: null,
      minimizedNewMessage: false,
      currentThreadId: null,
      unreadCounts: {},  // Track unread per thread_id
    };
  },
  watch: {
    selectedUserId() {
      this.loadChatHistory();
      this.connectToStream();
    },
  },
  methods: {
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      localStorage.setItem("chatMinimizedV2", JSON.stringify(this.isMinimized));
      
      // Reset unread count for current thread when opening
      if (!this.isMinimized && this.currentThreadId) {
        const threadKey = this.currentThreadId || 'general';
        this.unreadCounts[threadKey] = 0;
        const threadObj = this.threads.find(t => t.thread_id === this.currentThreadId);
        if (threadObj) {
          threadObj.unread_count = 0;
        }
      }
    },
    async loadCurrentUser() {
      try {
        const response = await getCurrentUser();
        this.currentUser = response.data;
        this.userType = response.data.user_type?.name.toLowerCase() || "student";
        this.currentUserId = String(response.data.id);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    },
    showNotification(title, body, threadId = null, userId = null) {
      if ("Notification" in window) {
        if (Notification.permission === "granted") {
          const notification = new Notification(title, { 
            body, 
            requireInteraction: true,
            tag: threadId || 'chat-message'
          });
          
          notification.onclick = async () => {
            window.focus();
            this.isMinimized = false;
            
            await this.loadThreads();
            
            const userIdNum = Number(userId);
            const thread = this.threads.find(t => 
              t.thread_id === threadId && t.other_user_id === userIdNum
            );
            
            if (thread) {
              this.selectThread(thread);
            } else {
              console.warn("[ChatWidget] Thread not found:", { threadId, userId });
            }
            
            notification.close();
          };
        } else {
          console.warn("[ChatWidget] Notification permission not granted:", Notification.permission);
        }
      } else {
        console.error("[ChatWidget] Notifications not supported in this browser");
      }
    },
    async loadThreads() {
      try {
        const response = await getChatThreads();
        this.threads = response.data.map(thread => {
          const threadKey = thread.thread_id || 'general';
          if (!Object.hasOwn(this.unreadCounts, threadKey)) {
            this.unreadCounts[threadKey] = 0;
          }
          return { ...thread, unread_count: this.unreadCounts[threadKey] };
        });
      } catch (error) {
        console.error("Failed to load chat threads:", error);
      }
    },
    handleIncomingMessage(msg) {
      const isOwnMessage = String(msg.sender_id) === this.currentUserId;
      const userId = String(msg.sender_id);
      const threadKey = msg.thread_id || 'general';

      const isCurrentChat = userId === String(this.selectedUserId) || isOwnMessage;
      
      const isInCurrentThread = !this.currentThreadId || msg.thread_id === this.currentThreadId;

      if (isCurrentChat && isInCurrentThread) {
        this.messages.push({
          ...msg,
          isOwnMessage,
          user_criterion_history: msg.user_criterion_history,
          team_criterion_history: msg.team_criterion_history,
        });
        this.$nextTick(() => {
          this.$refs.messageBox.scrollTop = this.$refs.messageBox.scrollHeight;
        });
        
        if (!this.isMinimized && isOwnMessage === false) {
          this.unreadCounts[threadKey] = 0;
        }
      } else if (!isOwnMessage) {
        this.unreadCounts[threadKey] = (this.unreadCounts[threadKey] || 0) + 1;
      }
      
      this.loadThreads();
      
      if (!isOwnMessage && this.userType === "professor") {
        let title = `New message from ${msg.from}`;
        let body = msg.text;
        
        if (msg.user_criterion_history) {
          const history = msg.user_criterion_history;
          title += ` - ${history.criterion_name}`;
          body = `Rating: ${history.int_value || history.bool_value || history.text_value}\n${msg.text}`;
        } else if (msg.team_criterion_history) {
          const history = msg.team_criterion_history;
          title += ` - [TEAM] ${history.criterion_name}`;
          body = `Rating: ${history.int_value || history.bool_value || history.text_value}\n${msg.text}`;
        }
        
        this.showNotification(title, body, msg.thread_id, userId, msg.from);
      }
    },
    selectThread(thread) {
      this.selectedUserId = thread.other_user_id;
      this.selectedUserName = thread.other_user_name;
      this.selectedCriterionName = thread.criterion_name;
      this.currentThreadId = thread.thread_id;
      
      const threadKey = thread.thread_id || 'general';
      
      this.unreadCounts[threadKey] = 0;
      
      const threadObj = this.threads.find(t => t.thread_id === thread.thread_id);
      if (threadObj) {
        threadObj.unread_count = 0;
      }
      
      this.loadChatHistory();
    },
    async loadChatHistory() {
      if (!this.selectedUserId || !this.currentUserId) {
        return;
      }

      this.isLoadingHistory = true;
      try {
        const response = await getChatHistory(this.selectedUserId, 50, this.currentThreadId);
        this.messages = response.data.map((m) => ({
          id: m.id,
          text: m.text,
          from: m.from,
          sender_id: m.sender_id,
          created_at: m.created_at,
          isOwnMessage: String(m.sender_id) === this.currentUserId,
          user_criterion_history: m.user_criterion_history,
          team_criterion_history: m.team_criterion_history,
          thread_id: m.thread_id,
        }));

        this.$nextTick(() => {
          const el = this.$refs.messageBox;
          if (el) el.scrollTop = el.scrollHeight;
        });
      } catch (error) {
        console.error("Error loading chat history:", error);
      } finally {
        this.isLoadingHistory = false;
      }
    },
    connectToStream() {
      if (!this.currentUserId || !this.selectedUserId) return;

      if (this.eventSource) this.eventSource.close();

      const token = localStorage.getItem("token");
      const apiBase = process.env.VUE_APP_API_URL || "/api";
      const url = `${apiBase}/chat/stream/${this.selectedUserId}?token=${token}`;

      this.eventSource = new EventSource(url);

      this.eventSource.onmessage = (ev) => {
        const data = JSON.parse(ev.data);
        if (data.type === "connected") return;
        this.handleIncomingMessage(data);
      };

      this.eventSource.onerror = (e) => {
        console.error("SSE error:", e);
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.eventSource = null;
        }
      };
    },
    async sendMessage() {
      if (!this.messageText.trim()) {
        console.warn("Message is empty, not sending.");
        return;
      }

      if (!this.selectedUserId || !this.currentUserId) {
        console.error("Missing user IDs. Cannot send message.");
        return;
      }

      try {
        let userHistoryId = null;
        let teamHistoryId = null;

        if (this.selectedHistoryReference) {
          if (this.selectedHistoryReference.source === 'team') {
            teamHistoryId = this.selectedHistoryReference.id;
          } else {
            userHistoryId = this.selectedHistoryReference.id;
          }
        }

        // Always send the current thread_id to keep messages in the same thread
        const response = await sendMessage(
          this.selectedUserId,
          this.messageText.trim(),
          userHistoryId,
          teamHistoryId,
          this.currentThreadId  // Pass current thread ID
        );
        console.log("Message sent successfully:", response.data);
        this.messageText = "";
        // Clear history reference display, but thread_id stays active
        this.selectedHistoryReference = null;
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    },
    setHistoryReference(reference) {
      this.selectedHistoryReference = reference;
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString();
    },
  },
  async mounted() {
    // Request notification permission for professors
    if ("Notification" in window && Notification.permission !== "granted") {
      await Notification.requestPermission();
    }
    
    const savedMinimized = localStorage.getItem("chatMinimizedV2");
    if (savedMinimized !== null) {
      this.isMinimized = JSON.parse(savedMinimized);
    }
    await this.loadCurrentUser();
    await this.loadThreads();

    this.globalEventSource = streamChatMessages();
    this.globalEventSource.onmessage = (ev) => {
      const data = JSON.parse(ev.data);
      if (data.type === "connected") return;

      const isOwnMessage = String(data.sender_id) === this.currentUserId;
      const isCurrentChat = String(data.sender_id) === String(this.selectedUserId) || isOwnMessage;

      if (!isCurrentChat && !isOwnMessage) {
        const threadKey = data.thread_id || 'general';
        // Increment unread count
        this.unreadCounts[threadKey] = (this.unreadCounts[threadKey] || 0) + 1;
        // Refresh threads to update UI
        this.loadThreads();
        
        // Show browser notification for professors for all messages from students
        if (this.userType === "professor") {
          let title = `New message from ${data.from}`;
          let body = data.text;
          
          // Add context if this is about a rating
          if (data.user_criterion_history) {
            const history = data.user_criterion_history;
            title += ` - ${history.criterion_name}`;
            body = `Rating: ${history.int_value || history.bool_value || history.text_value}\n${data.text}`;
          } else if (data.team_criterion_history) {
            const history = data.team_criterion_history;
            title += ` - [TEAM] ${history.criterion_name}`;
            body = `Rating: ${history.int_value || history.bool_value || history.text_value}\n${data.text}`;
          }
          
          this.showNotification(title, body, data.thread_id, data.sender_id, data.from);
        }
      }
    };
    this.globalEventSource.onerror = (e) => {
      console.error("Global SSE error:", e);
      if (this.globalEventSource?.readyState === EventSource.CLOSED) {
        this.globalEventSource = null;
      }
    };

    if (this.initialUserId && this.threads.length > 0) {
      const thread = this.threads.find((t) => t.other_user_id === Number(this.initialUserId));
      if (thread) this.selectThread(thread);
    }

    // Listen to chat events from other components
    if (this.eventBus) {
      this.eventBus.on("openChatWithHistory", async (data) => {
        // Ensure current user is loaded
        if (!this.currentUserId) {
          await this.loadCurrentUser();
        }
        
        // First refresh threads to ensure we have the latest
        await this.loadThreads();
        
        // Find the thread by user and history reference
        let threadId = null;
        if (data.history) {
          if (data.history.source === 'team' && data.history.id) {
            threadId = `team_history_${data.history.id}`;
          } else if (data.history.id) {
            threadId = `user_history_${data.history.id}`;
          }
        }
        
        // Find matching thread
        const thread = this.threads.find(t => 
          t.other_user_id === data.userId && 
          (!threadId || t.thread_id === threadId)
        );
        
        if (thread) {
          this.selectThread(thread);
        } else {
          // Fallback: create a temporary thread object
          this.selectThread({
            other_user_id: data.userId,
            other_user_name: data.userName,
            thread_id: threadId,
            criterion_name: data.history?.criterion_name || 'General Chat',
            latest_message: '',
            latest_message_time: new Date().toISOString(),
            unread_count: 0
          });
        }
        
        this.$nextTick(() => {
          this.setHistoryReference(data.history);
          this.isMinimized = false;
          
          // Reload chat history with thread filter
          this.loadChatHistory();
        });
      });
    }
  },

  beforeUnmount() {
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch (e) {
        console.error("Room EventSource close error:", e);
      }
    }

    if (this.globalEventSource) {
      try {
        this.globalEventSource.close();
      } catch (e) {
        console.error("Global EventSource close error:", e);
      }
    }

    if (this.eventBus) {
      this.eventBus.off("openChatWithHistory");
    }
  },
};
</script>

<style scoped>
div::-webkit-scrollbar {
  width: 6px;
}
div::-webkit-scrollbar-track {
  background: #f1f1f1;
}
div::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
div::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
